import { useState } from 'react'

import cn from 'classnames'
import { Link, useLocation, useNavigate } from 'react-router-dom'

import { ChainType } from '@lifi/types'
import { getConnectorIcon } from '@lifi/wallet-management'

import machLogo from '@src/assets/images/machLogo.svg'
import ConnectWallet from '@src/components/buttons/ConnectWallet'
import PendingTransactionsButton from '@src/components/buttons/PendingTransactionsButton'
import DiscordIcon from '@src/components/icons/DiscordIcon'
import TelegramIcon from '@src/components/icons/Telegram'
import TwitterIcon from '@src/components/icons/Twitter'
import { MENU_ITEMS } from '@src/configs/menu'
import * as PATH from '@src/configs/paths'
import { useWalletConnections } from '@src/hooks/useWalletConnections'
import { useClickOutside } from '@src/hooks/utils/useClickOutside'
import {
    AUDIT_LINK,
    BUG_FORM_LINK,
    CONTACT_LINK,
    DISCORD_LINK,
    FEEDBACK_FORM_LINK,
    TELEGRAM_LINK,
    TWITTER_LINK,
} from '@src/layouts/Footer'
import { useAuthContext } from '@src/providers/AuthProvider'
import { useEmbed } from '@src/providers/EmbedProvider'
import { IMenuItem } from '@src/types/menu'
import { truncateAddress } from '@src/utils/truncateAddress'
import { useApplicationStore } from '@src/zustand_stores/application.store'

// Define consistent wallet button styles
const walletButtonBaseClass =
    'flex items-center bg-mach-gray rounded-lg hover:brightness-110 h-9 header-wallet-button xs:h-8 xs:px-2'

const PlusButton = ({ className = '', onClick }: { className?: string; onClick: () => void }) => (
    <button
        onClick={onClick}
        className={`xs:h-8 xs:w-8 flex h-9 w-9 items-center justify-center rounded-lg bg-mach-gray transition-all hover:brightness-110 ${className}`}
        aria-label='Connect additional wallet'
    >
        <svg
            width='20'
            height='20'
            className='xs:w-4 xs:h-4'
            viewBox='0 0 24 24'
            fill='none'
            xmlns='http://www.w3.org/2000/svg'
        >
            <path d='M12 5V19M5 12H19' stroke='white' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' />
        </svg>
    </button>
)

interface HeaderProps {
    isEmbedMode?: boolean
    logoUrl?: string
}

const Header: React.FC<HeaderProps> = ({ isEmbedMode = false, logoUrl }) => {
    const navigate = useNavigate()
    const { pathname, search } = useLocation()
    const { connectHandler, accounts } = useAuthContext()
    const { isTronConnected, tronWalletAddress } = useWalletConnections()
    const openWalletDrawer = useApplicationStore(state => state.openWalletDrawer)
    const { isEmbed, showHamburgerMenu } = useEmbed()

    const [isMenuOpened, setIsMenuOpened] = useState<boolean>(false)
    const ref = useClickOutside(() => setIsMenuOpened(false))

    // Check if any wallet is connected and how many are connected
    const connectedWalletsCount = (accounts?.length || 0) + (isTronConnected && tronWalletAddress ? 1 : 0)
    const isAnyWalletConnected = connectedWalletsCount > 0
    const isSingleWalletConnected = connectedWalletsCount === 1

    const getCurrentPageTitle = () => {
        const currentMenuItem = MENU_ITEMS.find(
            menu => menu.navigation === pathname || (pathname === '/' && menu.label === 'Swap')
        )
        return currentMenuItem?.label || 'Swap'
    }

    // Render a connected wallet display
    const renderConnectedWallet = (address: string, icon: string, chainIcon: string, showAddress = true) => {
        return (
            <button
                className={`${walletButtonBaseClass} ${!showAddress ? 'w-9' : ''} cursor-pointer`}
                onClick={() => openWalletDrawer(true)}
            >
                <div className={`m-auto ${showAddress ? 'mr-2' : ''} flex items-center justify-center`}>
                    <div className='relative m-auto'>
                        <img
                            src={icon}
                            alt='wallet'
                            className='h-5 w-5 rounded-full'
                            onError={e => {
                                e.currentTarget.src = '/assets/token/unknown.png'
                            }}
                        />
                        {chainIcon && (
                            <div className='absolute -bottom-1 -right-1'>
                                <img
                                    src={chainIcon}
                                    alt='chain'
                                    className='h-3 w-3 rounded-full border border-[#1e1e1e]'
                                    onError={e => {
                                        e.currentTarget.src = '/assets/token/unknown.png'
                                    }}
                                />
                            </div>
                        )}
                    </div>
                </div>
                {showAddress && <span className='text-sm font-medium text-white'>{truncateAddress(address)}</span>}
            </button>
        )
    }

    // Render wallet icons without addresses for medium screens
    const renderWalletIconsView = () => (
        <div className='flex items-center gap-2 navbar-wallet-custom:hidden'>
            {/* First wallet icon if only one wallet connected */}
            {isSingleWalletConnected && accounts && accounts.length > 0 && accounts[0] && (
                <div>
                    {renderConnectedWallet(
                        String(accounts[0].address),
                        accounts[0].connector
                            ? getConnectorIcon(accounts[0].connector) || '/assets/token/unknown.png'
                            : '/assets/token/unknown.png',
                        accounts[0].chainType === ChainType.SVM
                            ? '/assets/network/solana.png'
                            : '/assets/network/ethereum.png',
                        false
                    )}
                </div>
            )}

            {/* If no EVM/Solana wallets but Tron is connected */}
            {isSingleWalletConnected &&
                (!accounts || accounts.length === 0) &&
                isTronConnected &&
                tronWalletAddress && (
                    <div>
                        {renderConnectedWallet(
                            tronWalletAddress,
                            '/assets/network/tron.png',
                            '/assets/network/tron.png',
                            false
                        )}
                    </div>
                )}

            {/* Multiple wallet icons */}
            {!isSingleWalletConnected &&
                accounts &&
                accounts
                    .filter(account => typeof account.address === 'string' && account.address.length > 0)
                    .map((account, index) => {
                        const walletIcon = account.connector
                            ? getConnectorIcon(account.connector)
                            : '/assets/token/unknown.png'
                        const chainIcon =
                            account.chainType === ChainType.SVM
                                ? '/assets/network/solana.png'
                                : '/assets/network/ethereum.png'

                        return (
                            <div key={`${account.address}-${index}`} className={index > 0 ? 'ml-1' : ''}>
                                {renderConnectedWallet(String(account.address), String(walletIcon), chainIcon, false)}
                            </div>
                        )
                    })}

            {/* Render Tron wallet icon without address for multiple wallets */}
            {!isSingleWalletConnected && isTronConnected && tronWalletAddress && (
                <div className={accounts && accounts.length > 0 ? 'ml-1' : ''}>
                    {renderConnectedWallet(
                        tronWalletAddress,
                        '/assets/network/tron.png',
                        '/assets/network/tron.png',
                        false
                    )}
                </div>
            )}

            {/* Plus button - show on medium screens when only 1 wallet connected */}
            {isSingleWalletConnected && <PlusButton onClick={connectHandler} />}
        </div>
    )

    // Render full wallet display for larger screens
    const renderFullWalletView = () => (
        <div className='hidden navbar-wallet-custom:flex navbar-wallet-custom:items-center navbar-wallet-custom:gap-2'>
            <ConnectWallet className='header-wallet-button h-9' />
            {/* Plus button for desktop view */}
            {isSingleWalletConnected && <PlusButton onClick={connectHandler} />}
        </div>
    )

    const renderWalletButtons = () => {
        if (window.location.pathname === PATH.ABOUT || isEmbedMode || isEmbed) return null

        // If no wallet is connected, just show the connect wallet button
        if (!isAnyWalletConnected) {
            return (
                <div className='left-container ml-0 hidden items-center justify-center gap-2 mobile-custom:order-2 mobile-custom:flex mobile-custom:justify-end mobile-custom:space-x-2'>
                    <PendingTransactionsButton />
                    <ConnectWallet className='header-wallet-button h-9' />
                </div>
            )
        }

        // For both single and multiple wallet connections
        return (
            <div className='left-container ml-0 flex items-center justify-center gap-2 mobile-custom:order-2 mobile-custom:ml-5 mobile-custom:justify-end mobile-custom:space-x-2'>
                <PendingTransactionsButton className='mobile-custom:mr-2' />
                {renderWalletIconsView()}
                {renderFullWalletView()}
            </div>
        )
    }

    if (isEmbedMode || isEmbed) {
        // Determine which pages should be full-width (only Orders) vs max-width
        const currentPath = window.location.pathname
        const isFullWidthPage = currentPath.includes('/orders') || currentPath === '/embed/orders'

        return (
            <div className='container z-50 flex justify-center' ref={ref}>
                <div className={`relative w-full ${!isFullWidthPage ? 'max-w-[520px]' : 'px-2 sm:px-4'} pt-4`}>
                    <div className='flex items-center gap-2'>
                        <div
                            className='left-0 top-4 flex h-[28px] w-min cursor-pointer items-center justify-center rounded-[6px] bg-[#212326] transition-all hover:brightness-110'
                            onClick={() => setIsMenuOpened(prev => !prev)}
                        >
                            <svg
                                width='14'
                                height='12'
                                viewBox='0 0 14 12'
                                fill='none'
                                xmlns='http://www.w3.org/2000/svg'
                                className='mx-[7px]'
                            >
                                <path
                                    d='M1 1.33337H6.33333'
                                    stroke='white'
                                    strokeWidth='1.33333'
                                    strokeLinecap='round'
                                    strokeLinejoin='round'
                                />
                                <path
                                    d='M1 6H9.66667'
                                    stroke='white'
                                    strokeWidth='1.33333'
                                    strokeLinecap='round'
                                    strokeLinejoin='round'
                                />
                                <path
                                    d='M1 10.6666H13'
                                    stroke='white'
                                    strokeWidth='1.33333'
                                    strokeLinecap='round'
                                    strokeLinejoin='round'
                                />
                            </svg>
                        </div>

                        {/* Add PendingTransactionsButton right next to the hamburger menu */}
                        <PendingTransactionsButton className='z-10 h-[28px] py-0 text-xs' />
                    </div>
                </div>

                {/* Mobile Sidebar Menu */}
                <div
                    className={cn(
                        'fixed left-0 top-0 z-50 flex h-full w-full max-w-xs flex-col bg-[#121212] transition-transform duration-300 ease-in-out',
                        isMenuOpened ? 'translate-x-0' : '-translate-x-full'
                    )}
                >
                    {/* Close button and logo */}
                    <div className='flex items-center justify-between p-4'>
                        <button
                            className='rounded-full bg-mach-gray p-2 text-white'
                            onClick={() => setIsMenuOpened(false)}
                        >
                            <svg
                                width='20'
                                height='20'
                                viewBox='0 0 24 24'
                                fill='none'
                                xmlns='http://www.w3.org/2000/svg'
                            >
                                <path
                                    d='M18 6L6 18M6 6L18 18'
                                    stroke='white'
                                    strokeWidth='2'
                                    strokeLinecap='round'
                                    strokeLinejoin='round'
                                />
                            </svg>
                        </button>
                        {logoUrl ? (
                            <div className='flex items-center justify-center'>
                                <img className='h-6' src={logoUrl} alt='Logo' />
                            </div>
                        ) : (
                            <div className='flex items-center justify-center'>
                                <img className='h-6' src={machLogo} alt='Mach Logo' />
                            </div>
                        )}
                    </div>

                    {/* Main menu items */}
                    <div className='space-y-6 px-10 py-4 text-xl text-[#a0a0a0]'>
                        {MENU_ITEMS.map((menu: IMenuItem, index: number) => {
                            // For embed mode, adjust navigation paths
                            let embedPath
                            if (menu.label === 'Swap') {
                                embedPath = '/embed'
                            } else if (
                                menu.label === 'Orders' ||
                                menu.label === 'Rewards' ||
                                menu.label === 'Fiat Exchange'
                            ) {
                                embedPath = menu.navigation.startsWith('/')
                                    ? `/embed${menu.navigation}`
                                    : `/embed/${menu.navigation}`
                            } else {
                                // For Docs, Developers, etc. - use original URL
                                embedPath = menu.navigation
                            }

                            // Preserve current URL search parameters for embed pages only
                            const currentSearch =
                                menu.label === 'Swap' ||
                                menu.label === 'Orders' ||
                                menu.label === 'Rewards' ||
                                menu.label === 'Fiat Exchange'
                                    ? window.location.search
                                    : ''

                            // Determine if link should open in new tab
                            const target = menu.label === 'Docs' || menu.label === 'Developers' ? '_blank' : menu.target

                            return (
                                <div key={index}>
                                    <Link
                                        className='block transition-colors hover:text-white'
                                        to={`${embedPath}${currentSearch}`}
                                        target={target}
                                        onClick={() => setIsMenuOpened(false)}
                                    >
                                        {menu.label}
                                        {target === '_blank' && (
                                            <span className='ml-1 inline-block'>
                                                <svg
                                                    className='h-3 w-3 text-[#959699] transition-colors group-hover:text-white'
                                                    fill='none'
                                                    stroke='currentColor'
                                                    viewBox='0 0 24 24'
                                                >
                                                    <path
                                                        strokeLinecap='round'
                                                        strokeLinejoin='round'
                                                        strokeWidth={2}
                                                        d='M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14'
                                                    />
                                                </svg>
                                            </span>
                                        )}
                                    </Link>
                                </div>
                            )
                        })}
                    </div>

                    {/* Bottom section with links and social media */}
                    <div className='mt-auto px-10 py-4'>
                        {/* Footer links */}
                        <div className='mb-8 flex flex-wrap gap-x-8 gap-y-2 text-[#a0a0a0]'>
                            <Link to={AUDIT_LINK} target='_blank' className='hover:text-white'>
                                Audit
                            </Link>
                            <Link to={CONTACT_LINK} className='hover:text-white'>
                                Contact
                            </Link>
                            <Link to={BUG_FORM_LINK} target='_blank' className='hover:text-white'>
                                Bugs
                            </Link>
                            <Link to={FEEDBACK_FORM_LINK} target='_blank' className='hover:text-white'>
                                Feedback
                            </Link>
                        </div>

                        {/* Copyright info */}
                        <div className='text-sm text-[#a0a0a0]'>
                            Copyright © {new Date().getFullYear()} Mach Protocol
                        </div>
                    </div>
                </div>

                {/* Overlay for mobile menu */}
                {isMenuOpened && (
                    <div className='fixed inset-0 z-40 bg-black/50' onClick={() => setIsMenuOpened(false)}></div>
                )}
            </div>
        )
    }

    return (
        <header
            className='container flex items-center justify-between py-6 font-geist max-mobile-custom:px-4'
            ref={ref}
        >
            <div className='flex flex-1 items-center'>
                {/* Menu button - show on mobile, positioned left of logo */}
                <div
                    className='xs:h-[24px] hidden h-[28px] w-min cursor-pointer items-center justify-center rounded-[6px] bg-[#212326] transition-all hover:brightness-110 max-mobile-custom:mr-4 max-mobile-custom:flex'
                    onClick={() => setIsMenuOpened(prev => !prev)}
                >
                    <svg
                        width='14'
                        height='12'
                        viewBox='0 0 14 12'
                        fill='none'
                        xmlns='http://www.w3.org/2000/svg'
                        className='mx-[7px]'
                    >
                        <path
                            d='M1 1.33337H6.33333'
                            stroke='white'
                            strokeWidth='1.33333'
                            strokeLinecap='round'
                            strokeLinejoin='round'
                        />
                        <path
                            d='M1 6H9.66667'
                            stroke='white'
                            strokeWidth='1.33333'
                            strokeLinecap='round'
                            strokeLinejoin='round'
                        />
                        <path
                            d='M1 10.6666H13'
                            stroke='white'
                            strokeWidth='1.33333'
                            strokeLinecap='round'
                            strokeLinejoin='round'
                        />
                    </svg>
                </div>
                <img className='h-6 w-auto cursor-pointer' src={machLogo} onClick={() => navigate(PATH.LANDING)} />
            </div>
            <nav className='flex flex-1 justify-center'>
                <div className='hidden text-[#edebeb] mobile-custom:flex mobile-custom:items-center mobile-custom:justify-center mobile-custom:gap-6'>
                    {MENU_ITEMS.map((menu: IMenuItem, index: number) => (
                        <Link
                            className={cn(
                                'flex h-9 cursor-pointer items-center whitespace-nowrap rounded-lg px-4 transition-colors',
                                menu.navigation === pathname || (pathname === '/' && menu.label === 'Swap')
                                    ? 'rounded-lg bg-mach-gray px-4'
                                    : 'hover:opacity-80'
                            )}
                            key={index}
                            to={menu.navigation}
                            target={menu.target}
                        >
                            <div className='flex items-center justify-between'>{menu.label}</div>
                        </Link>
                    ))}
                </div>
            </nav>

            <div className='flex flex-1 items-center justify-end'>{renderWalletButtons()}</div>

            {/* Mobile Sidebar Menu */}
            <div
                className={cn(
                    'fixed left-0 top-0 z-50 flex h-full w-full max-w-[360px] flex-col bg-[#121212] pl-5 pr-10',
                    isMenuOpened
                        ? 'translate-x-0 transition-transform duration-[200ms]'
                        : '-translate-x-full transition-transform delay-[100ms] duration-[500ms]'
                )}
            >
                {/* Close button and logo */}
                <div
                    className={cn(
                        'mb-3 flex items-center justify-between pt-6 transition-all duration-200',
                        isMenuOpened ? 'translate-x-0' : 'animate-menuContentSlideTop'
                    )}
                >
                    <button
                        className='rounded-[.375rem] bg-mach-gray p-2 text-white transition-colors hover:brightness-110'
                        onClick={() => setIsMenuOpened(false)}
                    >
                        <svg width='20' height='20' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
                            <path
                                d='M18 6L6 18M6 6L18 18'
                                stroke='white'
                                strokeWidth='2'
                                strokeLinecap='round'
                                strokeLinejoin='round'
                            />
                        </svg>
                    </button>
                    <div className='flex items-center justify-center'>
                        <img
                            className='h-6 cursor-pointer transition-opacity'
                            src={machLogo}
                            alt='Mach Logo'
                            onClick={() => {
                                setIsMenuOpened(false)
                                navigate(PATH.LANDING)
                            }}
                        />
                    </div>
                </div>

                {/* Logo and Title */}
                <div
                    className={cn(
                        'mb-5 mt-12 flex items-center justify-between transition-all duration-200',
                        isMenuOpened ? 'translate-x-0' : 'animate-menuContentSlideTop'
                    )}
                >
                    <h1 className='text-[36px] font-bold leading-[44px] text-white'>{getCurrentPageTitle()}</h1>
                    <div className='h-3 w-3 rounded-full bg-white'></div>
                </div>

                {/* Main menu items - excluding the selected one */}
                <div
                    className={cn(
                        'space-y-6 py-4 text-[1.5rem] leading-8 text-[#a0a0a0] transition-all duration-200',
                        isMenuOpened ? 'translate-x-0' : 'animate-menuContentSlideMid'
                    )}
                >
                    {MENU_ITEMS.filter(
                        menu => !(menu.navigation === pathname || (pathname === '/' && menu.label === 'Swap'))
                    ).map((menu: IMenuItem, index: number) => (
                        <div key={index}>
                            <Link
                                className='group block transition-colors hover:text-white'
                                to={menu.navigation}
                                target={menu.target}
                                onClick={() => setIsMenuOpened(false)}
                            >
                                <div className='flex items-center justify-between'>
                                    {menu.label}
                                    {menu.target === '_blank' && menu.label === 'Docs' && (
                                        <svg
                                            className='h-4 w-4 text-[#959699] transition-colors group-hover:text-white'
                                            fill='none'
                                            stroke='currentColor'
                                            viewBox='0 0 24 24'
                                        >
                                            <path
                                                strokeLinecap='round'
                                                strokeLinejoin='round'
                                                strokeWidth={2}
                                                d='M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14'
                                            />
                                        </svg>
                                    )}
                                </div>
                            </Link>
                        </div>
                    ))}
                </div>

                {/* Bottom section with links and social media */}
                <div
                    className={cn(
                        'mt-auto py-4 transition-all duration-200',
                        isMenuOpened ? 'translate-x-0' : 'animate-menuContentSlideBottom'
                    )}
                >
                    {/* Footer links */}
                    <div className='mb-8 flex flex-wrap gap-x-6 gap-y-2 text-[0.875rem] text-[#a0a0a0]'>
                        <Link to={AUDIT_LINK} target='_blank' className='hover:text-white'>
                            Audit
                        </Link>
                        <Link to={CONTACT_LINK} className='hover:text-white'>
                            Contact
                        </Link>
                        <Link to={PATH.STATS} className='hover:text-white'>
                            Stats
                        </Link>
                        <Link to={BUG_FORM_LINK} target='_blank' className='hover:text-white'>
                            Bugs
                        </Link>
                        <Link to={FEEDBACK_FORM_LINK} target='_blank' className='hover:text-white'>
                            Feedback
                        </Link>
                    </div>

                    {/* Social media icons */}
                    <div className='mb-6 flex gap-1.5'>
                        <Link
                            to={TWITTER_LINK}
                            target='_blank'
                            className='flex h-12 w-12 items-center justify-center rounded-lg bg-[#1a1a1a] transition-colors hover:bg-mach-foreground-hover'
                        >
                            <TwitterIcon className='h-5 w-5' />
                        </Link>
                        <Link
                            to={TELEGRAM_LINK}
                            target='_blank'
                            className='flex h-12 w-12 items-center justify-center rounded-lg bg-[#1a1a1a] transition-colors hover:bg-mach-foreground-hover'
                        >
                            <TelegramIcon className='h-5 w-5' />
                        </Link>
                        <Link
                            to={DISCORD_LINK}
                            target='_blank'
                            className='flex h-12 w-12 items-center justify-center rounded-lg bg-[#1a1a1a] transition-colors hover:bg-mach-foreground-hover'
                        >
                            <DiscordIcon className='h-5 w-5' />
                        </Link>
                        <Link
                            to={DISCORD_LINK}
                            target='_blank'
                            className='flex items-center justify-center gap-2 rounded-lg bg-[#1a1a1a] px-4 transition-colors hover:bg-mach-foreground-hover'
                        >
                            {/* Headphone icon for Support */}
                            <svg
                                width='20'
                                height='20'
                                viewBox='0 0 24 24'
                                fill='none'
                                xmlns='http://www.w3.org/2000/svg'
                                className='h-5 w-5'
                            >
                                <path
                                    d='M3 18V12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12V18'
                                    stroke='currentColor'
                                    strokeWidth='2'
                                    strokeLinecap='round'
                                    strokeLinejoin='round'
                                />
                                <path
                                    d='M21 19C21 20.1046 20.1046 21 19 21C17.8954 21 17 20.1046 17 19V16C17 14.8954 17.8954 14 19 14C20.1046 14 21 14.8954 21 16V19Z'
                                    stroke='currentColor'
                                    strokeWidth='2'
                                    strokeLinecap='round'
                                    strokeLinejoin='round'
                                />
                                <path
                                    d='M3 19C3 20.1046 3.89543 21 5 21C6.10457 21 7 20.1046 7 19V16C7 14.8954 6.10457 14 5 14C3.89543 14 3 14.8954 3 16V19Z'
                                    stroke='currentColor'
                                    strokeWidth='2'
                                    strokeLinecap='round'
                                    strokeLinejoin='round'
                                />
                            </svg>
                            <span>Support</span>
                        </Link>
                    </div>

                    {/* Copyright info */}
                    <div className='text-sm text-[#a0a0a0]'>Copyright © {new Date().getFullYear()} Mach Protocol</div>
                </div>
            </div>

            {/* Overlay for mobile menu */}
            {isMenuOpened && (
                <div className='fixed inset-0 z-40 bg-black/50' onClick={() => setIsMenuOpened(false)}></div>
            )}
        </header>
    )
}

export default Header
